import type { Timestamp } from "firebase/firestore";
import type { ReportFilters } from "./schema";
import type { PreparedImpactDisplay } from "./impact";

// Map loose time-like values to milliseconds
export function toMillisAny(v: any): number {
  if (v == null) return 0;
  if (typeof v === "number") return v;
  if (typeof v === "string") {
    const t = Date.parse(v);
    return Number.isFinite(t) ? t : 0;
  }
  if (v instanceof Date) return v.getTime();
  if (typeof (v as any)?.toMillis === "function") return (v as any).toMillis();
  if (typeof (v as any)?.seconds === "number") {
    return (v as any).seconds * 1000 + ((v as any).nanoseconds ? Math.floor((v as any).nanoseconds / 1e6) : 0);
  }
  return 0;
}

// Generic helper used when removing selections
export function toSelectedIds(sel: any): string[] {
  if (!sel) return [];
  if (Array.isArray(sel)) {
    return sel
      .map((x) => (typeof x === "string" ? x : x?.id))
      .filter(Boolean);
  }
  if (sel instanceof Set) {
    return Array.from(sel).filter((x): x is string => typeof x === "string");
  }
  if (typeof sel === "object") {
    return Object.keys(sel).filter((k) => !!(sel as any)[k]);
  }
  return [];
}

// Impact preview types
export type ImpactPreviewResult = {
  preview: PreparedImpactDisplay;
  itemIds?: string[];
};

export type ImpactPreviewFilters = ReportFilters & {
  productTypes?: unknown;
  customerIds?: unknown;
  basis?: unknown;
};

// Date parsing helpers used by impact preview
export function parseYMD_UTC(s: string): Date {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(Date.UTC(y, (m || 1) - 1, d || 1, 0, 0, 0, 0));
}

export function nextDayUTC(s: string): Date {
  const dt = parseYMD_UTC(s);
  dt.setUTCDate(dt.getUTCDate() + 1);
  return dt;
}

// Formaterar Date "YYYY-MM-DD"
export function toYMD(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// Visnings-format: ABC123*3 (oavsett hur det skrevs in)
export const formatSerialForDisplay = (serial?: string | null): string => {
  if (!serial) return "—";
  const [base, suffix] = String(serial).split("*");
  const norm = String(base || "").trim().toUpperCase().replace(/[\s\-_.:/\\]/g, "");
  return suffix ? `${norm}*${suffix}` : norm;
};

export function fmtUpdateTime(ms: number | null): string {
  if (!ms) return "—";
  return new Date(ms).toLocaleTimeString("sv-SE", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
}

export const toEpochMillis = toMillisAny;

// Shared UI constants
export const MAX_IMAGE_LONG_EDGE_PX = 1920;

// Status sort helper: 0=open, 1=ready, 2=invoiced
export function computeStateSort(completed?: boolean | null, invoiceReportId?: string | null): number {
  if (invoiceReportId) return 2;
  if (completed) return 1;
  return 0;
}
